import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Typo from '../../../components/atom/Typo';
import { Reveal } from 'react-awesome-reveal';
import { keyframes } from '@emotion/react';
import Button from '../../../components/atom/Button';
import colors from '../../../constants/colors.constants';
import { closeWebPage } from '../../../utils/utils';
import AppError from '../../../apis/models/common/res/AppError';
import useAlertStore from '../../../store/useAlertStore';
import { isValidChatRequest, requestChat } from '../../../apis/chat-request';
import useAuthStore from '../../../store/useAuthStore';
import { useQuery } from 'react-query';
import Spinner from '../../../components/atom/Spinner';
import strings from '../../../constants/strings.constants';

function ChatRequestPage() {
  const [searchParams] = useSearchParams();
  const [requesting, setRequesting] = useState(false);
  const { showAlert } = useAlertStore();
  const { partner } = useAuthStore();
  const partnerId = partner?.id || 0;
  const chatLogId = searchParams.get('chatLogId');
  const [isValidRequest, setIsValidRequest] = useState<boolean>(false);

  const { isLoading } = useQuery({
    queryKey: 'IS_VALID_CHAT_REQUEST',
    queryFn: async () => {
      if (chatLogId) {
        return isValidChatRequest(chatLogId);
      }
    },
    retry: false,
    enabled: !!chatLogId,
    refetchOnWindowFocus: false,
    onSuccess: () => {
      setIsValidRequest(true);
    },
    onError: (e) => {
      setIsValidRequest(false);
      const error = e as AppError;
      if (error.message) {
        showAlert(error.message, () => {
          closeWebPage();
        });
      }
    },
  });

  const revealTransition = keyframes`
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    
    to {
      opacity: 1;
      transform: translateY(0);
    }
  `;

  const isValidQueryParam = () => {
    try {
      const chatLogId = searchParams.get('chatLogId');
      const contextId = searchParams.get('contextId');
      if (chatLogId && contextId) {
        return parseInt(chatLogId) && parseInt(contextId);
      }
      return false;
    } catch (e) {
      return false;
    }
  };

  const requestConsultingChat = async () => {
    try {
      setRequesting(true);

      const chatLogId = searchParams.get('chatLogId');
      const contextId = searchParams.get('contextId');

      let chatLogIdParam = 0;
      let contextIdParam = 0;
      if (chatLogId && contextId) {
        chatLogIdParam = parseInt(chatLogId);
        contextIdParam = parseInt(contextId);
      }
      await isValidChatRequest(chatLogIdParam);
      await requestChat({
        chatLogId: chatLogIdParam,
        contextId: contextIdParam,
        partnerId,
      });
      showAlert(strings.completeChatRequest, () => {
        closeWebPage();
      });
    } catch (e) {
      const error = e as AppError;
      if (error.message) {
        showAlert(error.message);
      }
    } finally {
      setRequesting(false);
    }
  };

  return isValidQueryParam() ? (
    <div className="flex flex-col justify-center h-full">
      {isLoading ? (
        <Spinner />
      ) : (
        <Reveal keyframes={revealTransition} duration={300}>
          <div className="flex flex-col px-[20px] gap-2">
            <div>
              <Typo
                style={{
                  whiteSpace: 'pre-wrap',
                  lineHeight: 1.5,
                }}
                type="b-18"
              >
                {strings.chatRequestWelcomeTitle}
              </Typo>
            </div>
            <Typo
              color={colors.subTitle}
              type="r-16"
              style={{
                whiteSpace: 'pre-wrap',
              }}
            >
              {strings.chatRequestWelcomeDesc}
            </Typo>
            <div className="flex flex-col gap-2 mt-4">
              <Button
                disabled={requesting}
                onClick={async () => {
                  await requestConsultingChat();
                }}
              >
                {strings.connectChatRequired}
              </Button>
              <Button
                type="gray"
                onClick={() => {
                  closeWebPage();
                }}
              >
                {strings.connectChatLater}
              </Button>
            </div>
          </div>
        </Reveal>
      )}
    </div>
  ) : (
    <div className="p-[20px]">
      <Typo type="m-16" color={colors.subTitle}>
        올바른 접근 경로가 아닙니다.
      </Typo>
    </div>
  );
}

export default ChatRequestPage;
